/**
 * 拓扑引用量化容差。
 */
export const DEFAULT_TOPOLOGY_REF_TOLERANCE = 1e-6;

/**
 * 对法向量做量化时使用的默认容差。
 */
export const DEFAULT_TOPOLOGY_NORMAL_TOLERANCE = 1e-6;

/** `buildTopologyEdgeRef` 生成的量化坐标边引用格式。 */
const TOPOLOGY_EDGE_COORDINATE_REF_PATTERN = /^-?\d+,-?\d+,-?\d+\|-?\d+,-?\d+,-?\d+$/;

/**
 * 量化数值。
 *
 * @param value 原始数值。
 * @param tolerance 量化容差。
 * @returns 量化后的整数值。
 */
const quantizeTopologyValue = (value: number, tolerance: number): number => {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.round(value / tolerance);
};

/**
 * 序列化三维点。
 *
 * @param point 待序列化点。
 * @param tolerance 量化容差。
 * @returns 序列化结果。
 */
const serializeTopologyPoint = (point: TopologyPoint3D, tolerance: number): string => {
  return `${quantizeTopologyValue(point[0], tolerance)},${quantizeTopologyValue(point[1], tolerance)},${quantizeTopologyValue(point[2], tolerance)}`;
};

/**
 * 归一化法向量。
 *
 * @param normal 原始法向。
 * @returns 单位化后的法向；无法单位化时返回默认法向。
 */
const normalizeTopologyNormal = (normal: TopologyPoint3D): TopologyPoint3D => {
  const length = Math.sqrt(
    normal[0] * normal[0]
    + normal[1] * normal[1]
    + normal[2] * normal[2],
  );

  if (length <= Number.EPSILON) {
    return [0, 0, 1];
  }

  return [
    normal[0] / length,
    normal[1] / length,
    normal[2] / length,
  ];
};

/**
 * 构建权威子拓扑 ID。
 *
 * @param kind 子拓扑种类。
 * @param persistentRef 持久化引用。
 * @returns 稳定子拓扑 ID。
 */
export const buildTopologySubEntityId = (
  kind: TopologySubEntityKind,
  persistentRef: string,
): string => {
  if (persistentRef.startsWith(`${kind}:`)) {
    return persistentRef;
  }
  return `${kind}:${persistentRef}`;
};

/**
 * 构建特征结果体内部稳定子拓扑身份。
 *
 * @param kind 子拓扑种类。
 * @param featureId 当前结果体所属特征 ID。
 * @param topologyIndex 当前结果体内的局部拓扑索引。
 * @returns 稳定子拓扑身份。
 */
export const buildFeatureTopologySubEntityId = (
  kind: TopologySubEntityKind,
  featureId: string,
  topologyIndex: number,
): string => {
  return `${kind}:${featureId}:${topologyIndex}`;
};

/**
 * 构建边拓扑持久化引用。
 *
 * @param pointA 顶点 A。
 * @param pointB 顶点 B。
 * @param tolerance 量化容差。
 * @returns 稳定边引用。
 */
export const buildTopologyEdgeRef = (
  pointA: TopologyPoint3D,
  pointB: TopologyPoint3D,
  tolerance: number = DEFAULT_TOPOLOGY_REF_TOLERANCE,
): string => {
  const serializedPointA = serializeTopologyPoint(pointA, tolerance);
  const serializedPointB = serializeTopologyPoint(pointB, tolerance);

  return serializedPointA <= serializedPointB
    ? `${serializedPointA}|${serializedPointB}`
    : `${serializedPointB}|${serializedPointA}`;
};

/**
 * 判断字符串是否为基于端点量化坐标生成的边引用。
 *
 * 设计说明：坐标边引用能够跨越 Puppy CAD 内核 枚举顺序变化，而索引型子拓扑 ID 不能；
 * 需要在重建匹配链路中识别两类身份并建立明确优先级。
 *
 * @param persistentRef 待判断的持久化引用。
 * @returns 属于坐标边引用时返回 true。
 */
export const isTopologyEdgeCoordinateRef = (persistentRef: string): boolean => {
  return TOPOLOGY_EDGE_COORDINATE_REF_PATTERN.test(persistentRef);
};

/**
 * 构建顶点拓扑持久化引用。
 *
 * @param point 顶点坐标。
 * @param tolerance 量化容差。
 * @returns 稳定顶点引用。
 */
export const buildTopologyVertexRef = (
  point: TopologyPoint3D,
  tolerance: number = DEFAULT_TOPOLOGY_REF_TOLERANCE,
): string => {
  return `vertex:${serializeTopologyPoint(point, tolerance)}`;
};

/**
 * 构建面拓扑持久化引用。
 *
 * @param input 面签名或旧式面索引。
 * @param tolerance 点量化容差。
 * @param normalTolerance 法向量量化容差。
 * @returns 稳定面引用。
 */
export const buildTopologyFaceRef = (
  input: number | TopologyFaceSignature,
  tolerance: number = DEFAULT_TOPOLOGY_REF_TOLERANCE,
  normalTolerance: number = DEFAULT_TOPOLOGY_NORMAL_TOLERANCE,
): string => {
  if (typeof input === 'number') {
    return `face:${input}`;
  }

  const normalizedNormal = normalizeTopologyNormal(input.normal);
  return `face:${serializeTopologyPoint(input.point, tolerance)}@${serializeTopologyPoint(normalizedNormal, normalTolerance)}`;
};
